import {
	Base64FileUriUploadWithMetadataModel,
	FileMetadataModel,
	fileMetadataSymbol,
	FormattedTextString
} from "./commonTypes";
import { never } from "client_legacy/ndaw/errors";
import { createAbortError } from "client_legacy/ndaw/cancellation";

export function generateTrackingId(
	name: string,
	subName?: string
): string;
export function generateTrackingId(
	name: string | undefined,
	subName?: string
): string | undefined;
export function generateTrackingId(
	name: string | undefined,
	subName?: string
): string | undefined {
	return name
		? `${name}${subName ?? ""}`
		: undefined;
}

export function setTrackingId(
	name: string | undefined,
	subName?: string
): { ["data-tracking-id"]: string | undefined; }
{
	return {
		["data-tracking-id"]: generateTrackingId(name, subName)
	};
}

export function convertArbitraryValueForTrackingId(value: unknown): string {
	switch (typeof value) {
		case "object":
			return JSON.stringify(value);
		case "undefined":
		case "symbol":
		case "function":
			return typeof value;
		default:
			return `${value}`;
	}
}

export function delay(
	amountInMs: number,
	signal?: AbortSignal
): Promise<void> {
	return new Promise(
		(resolve, reject) => {
			let handle: SetTimeoutHandle;

			const cancellationCallback = () => {
				clearTimeout(handle);
				reject(
					createAbortError()
				);
			};

			handle = setTimeout(
				() => {
					signal?.removeEventListener("abort", cancellationCallback);
					resolve();
				},
				amountInMs
			);

			signal?.addEventListener("abort", cancellationCallback);
		}
	);
}

export function isNotNilOrEmpty(value: string | nil): value is string {
	return Boolean(value);
}

export function isNilOrWhiteSpace(value: string | nil): boolean {
	return isNil(value) || value.trim() === "";
}

export function isNotNilOrWhiteSpace(value: string | nil): value is string {
	return !isNilOrWhiteSpace(value);
}

export function isNil<T>(value: T): value is (T & nil) {
	return typeof value === "undefined" || value === null;
}

export function isNotNil<T>(value: T | nil): value is T {
	return typeof value !== "undefined" && value !== null;
}

export function ensureNotNil<T>(value: T | nil, message?: string): T {
	if (isNotNil(value)) {
		return value;
	}

	throw new Error(message ?? "Value should not be nil");
}

export const EMPTY_OBJECT: Readonly<{}> = Object.freeze({});
export const EMPTY_ARRAY: readonly [] = Object.freeze([]);

export function base64UrlEncode(
	value: string,
	truncatePadding: boolean = false
): string {
	let binary: string = "";
	const bytes: Uint8Array<ArrayBuffer> = new TextEncoder()
		.encode(value);

	for (const byte of bytes) {
		binary += String.fromCharCode(byte);
	}

	const result = btoa(binary)
		.replace(/\+/g, "-")
		.replace(/\//g, "_");

	if (truncatePadding) {
		return result.replace(/[=]+$/g, "");
	}

	return result;
}

export function base64UrlDecode(value: string): string {
	let base64: string = value
		.replace(/-/g, "+")
		.replace(/_/g, "/");

	let amountOfPaddingToAdd: number;

	switch (base64.length % 4) {
		case 0:
			amountOfPaddingToAdd = 0;
			break;
		case 1:
			throw new Error("Invalid base64url string");
		case 2:
			amountOfPaddingToAdd = 2;
			break;
		case 3:
			amountOfPaddingToAdd = 1;
			break;
		default:
			throw new Error ("Module 4 resulted in none supported remainder");
	}

	base64 += "=".repeat(amountOfPaddingToAdd);

	const binary: string = atob(base64);
	const bytes: Uint8Array<ArrayBuffer> = Uint8Array.from(binary, char => char.charCodeAt(0));

	return new TextDecoder().decode(bytes);
}

export function isArray<T>(arg: ReadonlyArray<T>|T): arg is ReadonlyArray<T> {
	return Array.isArray(arg);
}

export function resolveFormattedTextString(
	value: string | FormattedTextString
): string;
export function resolveFormattedTextString(
	value: string | FormattedTextString | nil
): string | undefined;
export function resolveFormattedTextString(
	value: string | FormattedTextString | nil
): string | undefined {
	if (isNil(value)) {
		return undefined;
	}

	if (typeof value === "string") {
		return value;
	}

	return value.replacements
		.reduce(
			(acc, cur) => {
				return acc.split(cur.what)
					.join(cur.filler);
			},
			value.template
		);
}

export function ensureSuccessfulResult<T>(
	result: TryResult<T>,
	errorMessage: string
): T {
	if (!result.wasSuccessful) {
		throw new Error(errorMessage);
	}

	return result.result;
}

export function formatDataAsCsv(
	headers: readonly string[],
	data: readonly (readonly string[])[]
): string {
	let result = "";
	const rows: readonly (readonly string[])[] = [headers, ...data];
	for (let i = 0; i < rows.length; i++) {
		const row: string = rows[i]
			.map((x: string) => {
				if (x.includes("\n")
					|| x.includes("\"")
					|| x.includes(",")) {
					return "\"" + x.replace(/\"/g, "\"\"") + "\"";
				}

				return x;
			})
			.join(",");

		result += row + "\r\n";
	}

	return result;
}

export function downloadAsFile(
	content: string,
	mimeType: string,
	filename: string
): void {
	const anchor: HTMLAnchorElement = document.createElement("a");
	anchor.setAttribute("href", `data:${mimeType};charset=utf-8,` + encodeURIComponent(content));
	anchor.setAttribute("download", filename);
	anchor.style
		.display = "none";

	document.body
		.appendChild(anchor);

	anchor.click();

	document.body
		.removeChild(anchor);
}

export function clamp(value: number, minValue?: number, maxValue?: number): number {
	if (isNotNil(maxValue)) {
		value = Math.min(value, maxValue);
	}

	if (isNotNil(minValue)) {
		value = Math.max(value, minValue);
	}

	return value;
}

export function getNumericEnumValues<TEnum extends {}>(object: TEnum): readonly TEnum[keyof TEnum][] {
	return Object.keys(object)
		.filter((x) => typeof (object as any)[x] === "number")
		.map((x) => (object as any)[x]);
}

export type Deferred<T> = {
	readonly promise: Promise<T>;
	readonly resolve: (value: T) => void;
	readonly reject: (reason?: unknown) => void;
};

export function deferred<T>(): Deferred<T> {
	let handlers: PromiseHandlers<T> | undefined;
	const promise = new Promise<T>(
		(resolve, reject) => {
			handlers = { resolve, reject };
		}
	);

	return {
		...ensureNotNil(handlers),
		promise,
	};
}

export function wrapPromiseWithCancellation<T>(
	promise: Promise<T>,
	signal?: AbortSignal
): Promise<T> {
	if (!signal) {
		return promise;
	}

	const result: Deferred<T> = deferred<T>();

	promise
		.then(
			result.resolve,
			result.reject
		);

	signal.addEventListener(
		"abort",
		(): void => result.reject(signal.reason),
		{ once: true }
	);

	return result.promise;
}

export function getFileMetadata(model: Base64FileUriUploadWithMetadataModel): FileMetadataModel {
	return model[fileMetadataSymbol];
}

export type RecordEntry<TRecord, TRecordKey extends StringKeyOf<TRecord>> = {
	key: TRecordKey;
	value: TRecord[TRecordKey];
};
export function entries<TRecord extends Record<string, unknown>>(
	record: TRecord
): readonly RecordEntry<TRecord, StringKeyOf<TRecord>>[] {
	const keys: string[] = Object.keys(record);
	return keys.map((key: string): RecordEntry<TRecord, StringKeyOf<TRecord>> => {
		const typedKey = key as StringKeyOf<TRecord>;
		return {
			key: typedKey,
			value: record[typedKey]
		};
	});
}

export type PropertyPathResolverNilBehavior = "propagate" | "throw";
type Result<TResult, TNilBehavior extends PropertyPathResolverNilBehavior> =
	TNilBehavior extends "propagate"
		? TResult | undefined
		: TResult;

export function getPropertyPathResolver<
	TValue,
	TResult,
	TNilBehavior extends PropertyPathResolverNilBehavior = "throw"
>(
	path: PropertyPath<TValue, TResult>,
	nilBehavior?: TNilBehavior
): (value: TValue) => Result<TResult, TNilBehavior> {
	let nilHandler: (propertyName: string, value: null | undefined) => void;

	switch (nilBehavior) {
		case "propagate":
			nilHandler = () => { /* NOOP */};
			break;
		case undefined:
		case "throw":
			nilHandler = (propertyName: string, value: null | undefined): never => {
				const parentName: "null" | "undefined" = value === null
					? "null"
					: value === undefined
						? "undefined"
						: never(value);

				throw new TypeError(`Cannot read properties of ${parentName} (reading '${propertyName}')`);
			};
			break;
		default:
			never(nilBehavior);
	}

	const properties: string[] = path.split(".");

	return (value: TValue): TResult => {
		let current: unknown = value;

		for (const property of properties) {
			if (isNil(current)) {
				nilHandler(property, current);

				// NOTE For optimization purposes, if nilHandler didn't throw an exception we assume
				// the nilBehavior is to propagate
				return undefined as TResult;
			}

			current = (current as any)[property];
		}

		return current as TResult;
	};
}

export function hasFlag<TFlagEnum extends number>(value: TFlagEnum, flag: TFlagEnum) {
	return (value & flag) === flag;
}

export function capitalizeFirstLetter(message: string): string {
	return message.charAt(0).toUpperCase() + message.slice(1);
}

export function lowerCaseFirstCharacter(value: string) {
	if (value.length === 0) {
		return value;
	}
	return value.charAt(0).toLowerCase() + value.slice(1);
}

export function upperCaseFirstCharacter(value: string) {
	if (value.length === 0) {
		return value;
	}
	return value.charAt(0).toUpperCase() + value.slice(1);
}

export function debounce<T extends Function>(
	action: T,
	waitInMs: number,
	signal?: AbortSignal
): T {
	let handle: SetTimeoutHandle | undefined;
	let result: unknown;

	const debouncedAction = function (this: unknown, ...args: unknown[]): unknown {
		clearTimeout(handle);
		const context: unknown = this;

		handle = setTimeout(
			() => {
				result = action.apply(context, args);
			},
			waitInMs
		);

		return result;
	};

	if (signal) {
		signal.addEventListener(
			"abort",
			(): void => {
				clearTimeout(handle);
			}
		);
	}

	return debouncedAction as unknown as T;
}

export function throttle<T extends Function>(
	action: T,
	waitInMs: number
): T {
	let result: unknown;
	let handle: SetTimeoutHandle;
	let lastTimeRunInMs: number | undefined;

	const throttledAction = function (this: unknown, ...args: unknown[]): unknown {
		const context = this;

		clearTimeout(handle);

		const nowInMs = new Date().getTime();

		const run = (): void => {
			lastTimeRunInMs = new Date().getTime();
			result = action.apply(context, args);
		};

		if (!lastTimeRunInMs || (nowInMs - lastTimeRunInMs) > waitInMs) {
			run();
		} else {
			handle = setTimeout(
				run,
				waitInMs - (nowInMs - lastTimeRunInMs)
			);
		}

		return result;
	};

	return throttledAction as unknown as T;
}

export function escapeForRegex(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function getDataUrl(file: File | Blob): Promise<string> {
	return new Promise<string>(
		(resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const dataUrl: string = reader.result as string;
				resolve(dataUrl);
			};

			reader.onerror = () => reject();

			reader.readAsDataURL(file);
		}
	);
}

export function getRandomInt(min: number, max: number): number {
	const minCeiled: number = Math.ceil(min);
	const maxFloored: number = Math.floor(max);

	return Math.floor(
		Math.random() * (maxFloored - minCeiled) + minCeiled
	);
}
